import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box, Checkbox, Divider, FormControlLabel, Grid, IconButton, MenuItem, TextField, Typography,
} from '@mui/material';
import React, { ChangeEvent, useEffect, useState } from 'react';
import CustomButton from '../../../../../components/General/CustomButton';
import { VarietySchema } from '../../../../../requests/api/apiTypes';
import { ignoreDateTimezone } from '../../../../../utils/formatDate';
import { ModalUpsertCultivationCycleSchema, SimplifiedCultivationCycle } from '../../types';
import useStyles from '../styles';

const UpsertCultivationCycleModalContent = ({
  handleCloseModal,
  handleUpsertCycleFunction,
  varietiesList,
  speciesList,
  cycleId,
  cultivationCycle,
}: {
  handleCloseModal: VoidFunction,
  handleUpsertCycleFunction: (cultivationCycle: ModalUpsertCultivationCycleSchema) => void,
  varietiesList: VarietySchema[],
  speciesList: { id?: string; name?: string }[],
  cycleId?: string,
  cultivationCycle?: SimplifiedCultivationCycle,
}) => {
  const classes = useStyles();

  const [
    currentCultivationCycle,
    setCurrentCultivationCycle,
  ] = useState<ModalUpsertCultivationCycleSchema>(
    {
      name: '',
      startDate: '',
      endDate: '',
      varietyId: '',
      useDefaultPhenologicalStates: false,
      producedKg: undefined,
      exportablePercentage: undefined,
      productionQuality: undefined,
    },
  );

  const [selectedSpecie, setSelectedSpecie] = useState('');
  const [varietiesOptions, setVarietiesOptions] = useState<VarietySchema[]>([]);

  useEffect(() => {
    if (cycleId && cultivationCycle) {
      const currentVariety = varietiesList.find((variety) => (
        variety.name === cultivationCycle.varietyName
        && variety.species === cultivationCycle.varietySpecies
      ));
      setCurrentCultivationCycle({
        name: cultivationCycle.name,
        startDate: cultivationCycle.startDate.split('T')[0],
        endDate: cultivationCycle.endDate.split('T')[0],
        varietyId: currentVariety?.id ?? '',
        useDefaultPhenologicalStates: false,
        producedKg: cultivationCycle.producedKg,
        exportablePercentage: cultivationCycle.exportablePercentage,
        productionQuality: cultivationCycle.productionQuality,
      });

      setSelectedSpecie(currentVariety?.species || '');
      setVarietiesOptions(
        varietiesList.filter((variety) => variety.species === currentVariety?.species) || [],
      );
    }
  }, [cycleId, cultivationCycle, varietiesList]);

  const handleSpecieChange = (e: ChangeEvent<HTMLInputElement>) => {
    const specie = e.target.value;
    setSelectedSpecie(specie);
    const filteredVarieties = varietiesList.filter((option) => option.species === specie);
    setVarietiesOptions(filteredVarieties);
    setCurrentCultivationCycle({
      ...currentCultivationCycle,
      varietyId: '',
    });
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCurrentCultivationCycle({
      ...currentCultivationCycle,
      useDefaultPhenologicalStates: e.target.checked,
    });
  };

  const checkboxLabel = cycleId && cultivationCycle
    ? 'Usar estados fenológicos de la variedad seleccionada\n(Borrará los estados anteriores)'
    : 'Utilizar estados fenológicos estándar';

  const handleVarietyChange = (e: ChangeEvent<HTMLInputElement>) => {
    const varietyId = e.target.value;
    setCurrentCultivationCycle({
      ...currentCultivationCycle,
      varietyId,
    });
  };

  const handleUpsertCultivationCycle = () => {
    handleUpsertCycleFunction({
      name: currentCultivationCycle.name,
      startDate: currentCultivationCycle.startDate,
      endDate: currentCultivationCycle.endDate,
      varietyId: currentCultivationCycle.varietyId,
      useDefaultPhenologicalStates:
        currentCultivationCycle.useDefaultPhenologicalStates,
      producedKg: currentCultivationCycle.producedKg,
      exportablePercentage:
        currentCultivationCycle.exportablePercentage,
      productionQuality:
        currentCultivationCycle.productionQuality,
    });
  };

  const numericFields = [
    'producedKg', 'exportablePercentage', 'productionQuality',
  ];

  const handleDataChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (numericFields.includes(name)) {
      setCurrentCultivationCycle({
        ...currentCultivationCycle,
        [name]: value === '' ? undefined : Number(value),
      });
    } else if (name !== 'name') {
      setCurrentCultivationCycle({
        ...currentCultivationCycle,
        [name]: ignoreDateTimezone(value),
      });
    } else {
      setCurrentCultivationCycle({
        ...currentCultivationCycle,
        [name]: value ?? null,
      });
    }
  };

  return (
    <Box className={classes.upsertCropCycleModalContainer}>
      <Box className={classes.upsertCropCycleModalHeader}>
        <Typography
          variant="h3"
          color="textPrimary"
          className={classes.upsertCropCycleModalHeaderTitle}
        >
          <span className={classes.activePageUnderlined}>
            Agregar ciclo de cultivo
          </span>
        </Typography>
        <IconButton onClick={handleCloseModal}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider className={classes.dividerStyle} />
      <Grid container spacing={2}>
        <Grid item sm={12} md={12}>
          <TextField
            label="Nombre"
            name="name"
            value={currentCultivationCycle.name}
            onChange={handleDataChange}
            fullWidth
            variant="standard"
            InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item sm={6} md={6}>
          <TextField
            label="Fecha de inicio"
            type="date"
            name="startDate"
            value={currentCultivationCycle.startDate}
            onChange={handleDataChange}
            fullWidth variant="standard"
            InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item sm={6} md={6}>
          <TextField
            label="Fecha de fin"
            type="date"
            name="endDate"
            value={currentCultivationCycle.endDate}
            onChange={handleDataChange}
            fullWidth
            variant="standard"
            InputLabelProps={{ shrink: true }} />
        </Grid>

        <Grid item sm={6} md={6}>
          <TextField
            label="Especie"
            select
            fullWidth
            value={selectedSpecie}
            onChange={handleSpecieChange}
            variant="standard"
          >
            {speciesList
              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
              .map((species) => (
                <MenuItem key={species.id} value={species.name}>
                  {species.name}
                </MenuItem>
              ))}
          </TextField>
        </Grid>

        <Grid item sm={6} md={6}>
          <TextField
            label="Variedad"
            select
            fullWidth
            value={currentCultivationCycle.varietyId}
            onChange={handleVarietyChange}
            variant="standard"
            disabled={!selectedSpecie}
          >
            {varietiesOptions.map((variety) => (
              <MenuItem key={variety.id} value={variety.id}>
                {variety.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item sm={4} md={4}>
          <TextField
            label="Kg producidos"
            type="number"
            name="producedKg"
            value={currentCultivationCycle.producedKg ?? ''}
            onChange={handleDataChange}
            fullWidth
            variant="standard"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: 0 }} />
        </Grid>
        <Grid item sm={4} md={4}>
          <TextField
            label="% Exportable"
            type="number"
            name="exportablePercentage"
            value={currentCultivationCycle.exportablePercentage ?? ''}
            onChange={handleDataChange}
            fullWidth
            variant="standard"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: 0, max: 100 }} />
        </Grid>
        <Grid item sm={4} md={4}>
          <TextField
            label="Calidad (1-5)"
            type="number"
            name="productionQuality"
            value={currentCultivationCycle.productionQuality ?? ''}
            onChange={handleDataChange}
            fullWidth
            variant="standard"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: 1, max: 5 }} />
        </Grid>
        <Grid item>
          <FormControlLabel control={
            <Checkbox
              name="useStandardPhenologicalStates"
              checked={currentCultivationCycle.useDefaultPhenologicalStates}
              onChange={handleCheckboxChange}
            />
          }
            label={checkboxLabel}
          />
        </Grid>
      </Grid>
      <Box className={classes.upsertCropCycleModalButtonsContainer}>
        <Box width='100%' mr={2}><CustomButton color='blue' buttonText='Cancelar' onClick={handleCloseModal} /></Box>
        <Box width='100%' ml={2}><CustomButton color='green' buttonText={cycleId ? 'Editar' : 'Agregar'} onClick={handleUpsertCultivationCycle} /></Box>
      </Box>
    </Box>
  );
};

export default UpsertCultivationCycleModalContent;
